import { getApiWorkspaceVersion, subscribeApiWorkspace } from '../api/workspaceState'

export function requestAborted() {
  const error = new Error('Request aborted')
  error.name = 'AbortError'
  return error
}

/** Cancellation is advisory: current() also rejects late success/error/finally. */
export function createRequestScope({ workspaceBound = true }: { workspaceBound?: boolean } = {}) {
  let active = true
  let lifetime = 0
  let activeKey = ''
  let activation = 0
  const requests = new Map<string, AbortController>()
  const cancel = () => {
    lifetime++
    for (const controller of requests.values()) controller.abort()
    requests.clear()
  }
  let unsubscribe: (() => void) | undefined
  return {
    activate(key = '') {
      active = true
      activeKey = key
      const binding = ++activation
      unsubscribe?.()
      const version = getApiWorkspaceVersion()
      unsubscribe = workspaceBound ? subscribeApiWorkspace(() => {
        if (binding === activation && version !== getApiWorkspaceVersion()) { active = false; cancel() }
      }) : undefined
    },
    cancel,
    dispose() { active = false; activation++; cancel(); unsubscribe?.(); unsubscribe = undefined },
    begin(channel: string, key = '') {
      if (!active || key !== activeKey) {
        const controller = new AbortController(); controller.abort()
        return { signal: controller.signal, current: () => false, owned: () => false, finish() { }, cancel() { } }
      }
      requests.get(channel)?.abort()
      const controller = new AbortController()
      const version = getApiWorkspaceVersion()
      const generation = lifetime
      requests.set(channel, controller)
      const owned = () => active && lifetime === generation && requests.get(channel) === controller
      const current = () => owned() && !controller.signal.aborted
        && (!workspaceBound || version === getApiWorkspaceVersion())
      return {
        signal: controller.signal,
        current,
        owned,
        finish() { if (requests.get(channel) === controller) requests.delete(channel) },
        cancel() { controller.abort() },
      }
    },
  }
}
